import { Paper, OutlinedInput, InputAdornment, IconButton, Icon, Button, Dialog, DialogContent, DialogContentText, List, ListItem, Typography, Box } from '@mui/material'
import { KeyboardEvent, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { convertToPixelFromVH, convertToPixelFromVW } from '../../utils/ViewportUtils';

function PromptInput({ disable = false, placeholder, suggestions = [], onPromptSubmit }:
    { disable?: boolean, placeholder?: string, suggestions?: string[], onPromptSubmit: (query: string) => void }) {

    const [query, setQuery] = useState('');

    const handleEnterKey = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            if (query.length === 0) return;
            onPromptSubmit(query);
            setQuery("");
        }
    }

    const handleSuggestionSelection = (suggestion: string) => {
        setQuery(suggestion);
        setOpen(false);
    }

    const [open, setOpen] = useState(false);
    const dialogWidth = 37.5;

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            {suggestions?.length > 0 &&
                <>
                    <Button variant={'text'}
                        onClick={handleClickOpen}
                        sx={{
                            width: 'max-content',
                            alignSelf: 'flex-end',
                            color: '#00A9B1',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            padding: 0,
                            lineHeight: 1,
                        }}>
                        See Suggested Prompts
                    </Button>
                    <Dialog
                        open={open}
                        onClose={handleClose}
                        PaperProps={{
                            sx: {
                                display: 'grid',
                                placeContent: 'center'
                            }
                        }}
                        sx={{
                            background: '#CCCCCC87',
                            '& .MuiDialog-paper': {
                                boxShadow: 'none',
                                filter: 'drop-shadow(0px 0px 2px #009CA4) drop-shadow(0px 0px 15px black)',
                                backgroundColor: 'transparent !important'
                            }
                        }}>

                        <DialogContent sx={{
                            boxSizing: 'border-box',
                            width: convertToPixelFromVW(dialogWidth),
                            height: '50vh',
                            background: 'var(--text-light)',
                            clipPath: `path('M ${convertToPixelFromVW(1.5)} 0 H ${convertToPixelFromVW(37.5)} V ${convertToPixelFromVH(47.5)}  L ${convertToPixelFromVW(36)} ${convertToPixelFromVH(50)} H 0 V ${convertToPixelFromVH(2.5)} Z')`,
                        }}>
                            <DialogContentText component={'div'}>
                                <Box sx={{
                                    width: convertToPixelFromVW(dialogWidth - 2),
                                    position: 'absolute',
                                    zIndex: 1,
                                    top: 0,
                                    paddingTop: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    background: 'var(--text-light)'
                                }}>
                                    <Typography variant='h5' sx={{
                                        width: 'max-content',
                                        color: '#000000',
                                        borderBottom: '2px solid #000000'
                                    }}>Suggested prompts</Typography>

                                    <IconButton disableRipple
                                        onClick={handleClose}
                                        sx={{
                                            transition: 'all 200ms ease-in-out',
                                            '&:hover': {
                                                scale: 1.25,
                                            }
                                        }}>
                                        <CloseIcon htmlColor='#000000' />
                                    </IconButton>
                                </Box>
                                <List sx={{
                                    marginTop: '4vh',
                                }}>
                                    {suggestions.length > 0 && suggestions.map((suggestion: string, index: number) => {
                                        return <ListItem
                                            key={index}
                                            onClick={() => handleSuggestionSelection(suggestion)}
                                            sx={{
                                                background: '#DFF8FF99',
                                                fontWeight: 500,
                                                marginBottom: 2,
                                                transition: 'background 200ms ease-in-out',
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                    background: '#DFF8FF'
                                                },
                                                '&::after': {
                                                    content: '" "',
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 0,
                                                    width: '30px',
                                                    height: '30px',
                                                    background: 'var(--text-light)',
                                                    clipPath: "path('M 30 0 V 30 H 0 Z')"
                                                }
                                            }}>
                                            <Typography variant='body1' fontSize={'1.125rem'}>
                                                {suggestion}
                                            </Typography>
                                        </ListItem>
                                    })}
                                </List>
                            </DialogContentText>
                        </DialogContent>
                    </Dialog>
                </>
            }
            <Paper elevation={0}
                sx={{
                    width: '100%',
                    bgcolor: 'transparent',
                }}>
                <OutlinedInput
                    disabled={disable}
                    className="input_prompt"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    fullWidth={true}
                    placeholder={placeholder ?? 'Ask a question...'}
                    autoFocus={false}
                    onKeyDown={handleEnterKey}
                    sx={{
                        paddingX: 1,
                        fontWeight: "bold",
                        color: 'var(--text-primary)',
                        backdropFilter: 'saturate(120%) blur(10px);',
                        outline: "none",
                        borderRadius: '0.5vw',
                        bgcolor: 'rgba(255,255,255, 5%)',
                        border: '0.125vw solid #009CA4CC',
                        boxShadow: '0 0 0.125vw 0 #0000001A, 0px 0px 0.5vw 0px #3ED4DC',
                        "& fieldset": { border: 'none' },
                        '&>input': {
                            paddingY: 1.5,
                        }
                    }}
                    startAdornment={
                        <Icon sx={{
                            background: '#F2F8FD85',
                            borderRadius: 20,
                            padding: 0.5,
                            boxShadow: '0px 0px 0.125vw 0.125vw #12AEB599 inset',
                            marginRight: 1,
                            '& svg': {
                                width: '1vw',
                                transform: 'rotate(180deg)'
                            }
                        }}>
                            <AutoAwesomeIcon htmlColor='#00A9B1' />
                        </Icon>
                    }
                    endAdornment={
                        <InputAdornment position='end' sx={{
                            color: '#211E4E'
                        }}>
                            <IconButton
                                disabled={disable}
                                color='inherit'
                                onClick={() => {
                                    if (query.length === 0) return;
                                    onPromptSubmit(query);
                                    setQuery("");
                                }}>
                                <ArrowForwardIosIcon fontSize='small' />
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </Paper>
        </>
    )
}

export default PromptInput